import {
  AmountUnitTypeEnum,
  DiscountStatusEnum,
  IDiscount,
} from '@monetize/types/app';
import { createColumnHelper } from '../../../components/Monetize/DataTableNew/appTable';
import { MText } from '../../../components/Monetize';
import { actionCatalogColumn } from '../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  getCurrencyFilterValues,
  nameWithIdBodyTemplate,
  textBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import { AMOUNT_UNIT_DISPLAY_TEXT } from '../../../constants/amountUnits';
import { DISCOUNT_STATUS_ENUM_DISPLAY } from '../../../constants/discounts';
import { getDiscountEditRoute } from '../../../constants/routes';
import { getDiscountText } from '../../../utils';

const columnHelper = createColumnHelper<IDiscount>();

export type DiscountListColumnContext = ColumnContextBase<IDiscount>;

/** tableId: FilterStateKeys.DISCOUNT_LIST */
export const DISCOUNT_LIST_CATALOG: ColumnCatalog<
  IDiscount,
  DiscountListColumnContext
> = [
  {
    id: 'name',
    label: 'Name',
    customizable: false,
    build: () =>
      columnHelper.accessor('name', {
        ...getColHeaderWithSize('Name', 300),
        cell: nameWithIdBodyTemplate<IDiscount, unknown>({
          idProp: 'id',
          nameProp: 'name',
          idLinkFn: (id) => getDiscountEditRoute(id),
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'discountCode',
    label: 'Code',
    build: () =>
      columnHelper.accessor('discountCode', {
        ...getColHeaderWithSize('Code', 200),
        ...getColHeaderFilters('TEXT'),
        cell: textBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'discountType',
    label: 'Type',
    build: () =>
      columnHelper.accessor('discountType', {
        ...getColHeaderWithSize('Type', 200),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            AmountUnitTypeEnum,
            AMOUNT_UNIT_DISPLAY_TEXT,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: AMOUNT_UNIT_DISPLAY_TEXT,
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'status',
    label: 'Status',
    build: () =>
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 200),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            DiscountStatusEnum,
            DISCOUNT_STATUS_ENUM_DISPLAY,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: DISCOUNT_STATUS_ENUM_DISPLAY,
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'currency',
    label: 'Currency',
    build: () =>
      columnHelper.accessor('currency', {
        ...getColHeaderWithSize('Currency', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getCurrencyFilterValues(),
        }),
        cell: textBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'discountAmount',
    label: 'Amount',
    build: () =>
      columnHelper.accessor('discountAmount', {
        ...getColHeaderWithSize('Amount'),
        ...getColHeaderFilters('NUMBER'),
        cell: ({ row }) => (
          <MText lineClamp={1}>
            {getDiscountText(
              row.original.discountAmount,
              row.original.discountType,
            )}
          </MText>
        ),
        enableSorting: true,
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        header: 'Updated',
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        size: 180,
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        header: 'Created',
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        size: 180,
        enableSorting: true,
      }),
  },
  actionCatalogColumn<IDiscount>(),
];
